import React, { useState, useEffect } from 'react';
import { Card, Button, message, Modal, Spin, Alert, Typography, Space } from 'antd';
import { QrcodeOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { QRCodeCanvas } from 'qrcode.react';

const { Title, Text } = Typography;

// 配置axios默认URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const QRPayment = () => {
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, paid, failed
  const [countdown, setCountdown] = useState(0);
  const [orderInfo, setOrderInfo] = useState(null);

  // 从URL参数获取订单信息
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('orderId');
    const orderNo = urlParams.get('orderNo');
    
    if (orderId || orderNo) {
      setOrderInfo({ orderId, orderNo });
      createQRPayment(orderId, orderNo);
    }
  }, []);

  // 创建扫码支付
  const createQRPayment = async (orderId, orderNo) => {
    setLoading(true);
    try {
      const response = await axios.post('/card-sales/qr-payment', {
        orderId: orderId,
        orderNo: orderNo
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'User-ID': user?.id,
          'User-Role': user?.role
        }
      });

      if (response.data.success) {
        setPaymentData(response.data);
        setCountdown(900); // 15分钟倒计时
        message.success('支付二维码已生成，请使用微信扫描');
        
        // 开始轮询支付状态
        startPaymentStatusPolling(orderId || orderNo);
      } else {
        message.error(response.data.message || '生成支付二维码失败');
      }
    } catch (error) {
      console.error('创建扫码支付失败:', error);
      message.error('创建扫码支付失败');
    } finally {
      setLoading(false);
    }
  };

  // 轮询支付状态
  const startPaymentStatusPolling = (orderId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/card-sales/payment-status/${orderId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'User-ID': user?.id,
            'User-Role': user?.role
          }
        });

        if (response.data.success) {
          const status = response.data.paymentStatus;
          if (status === 'paid') {
            setPaymentStatus('paid');
            clearInterval(pollInterval);
            message.success('支付成功！会员卡已激活');
            
            // 3秒后跳转到首页
            setTimeout(() => {
              window.location.href = '/';
            }, 3000);
          } else if (status === 'failed') {
            setPaymentStatus('failed');
            clearInterval(pollInterval);
            message.error('支付失败或已取消');
          }
        }
      } catch (error) {
        console.error('查询支付状态失败:', error);
      }
    }, 3000); // 每3秒查询一次

    // 15分钟后停止轮询
    setTimeout(() => {
      clearInterval(pollInterval);
      if (paymentStatus === 'pending') {
        setPaymentStatus('failed');
        message.error('支付超时，请重新生成二维码');
      }
    }, 900000);
  };

  // 倒计时
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 重新生成二维码
  const regenerateQR = () => {
    if (orderInfo) {
      createQRPayment(orderInfo.orderId, orderInfo.orderNo);
    }
  };

  // 返回小程序
  const backToMiniProgram = () => {
    if (window.wx && window.wx.miniProgram) {
      window.wx.miniProgram.navigateBack();
    } else {
      window.history.back();
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Card style={{ 
        maxWidth: '500px', 
        width: '100%',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: 'none'
      }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* 标题 */}
            <div>
              <QrcodeOutlined style={{ 
                fontSize: '48px', 
                color: '#1890ff',
                marginBottom: '16px'
              }} />
              <Title level={2} style={{ marginBottom: '8px' }}>
                扫码支付
              </Title>
              <Text type="secondary">
                请使用微信扫描下方二维码完成支付
              </Text>
            </div>

            {/* 支付状态 */}
            {paymentStatus === 'pending' && (
              <Alert
                message="等待支付"
                description={`请在 ${formatTime(countdown)} 内完成支付`}
                type="info"
                showIcon
              />
            )}
            
            {paymentStatus === 'paid' && (
              <Alert
                message="支付成功"
                description="会员卡已激活，正在跳转..."
                type="success"
                showIcon
              />
            )}
            
            {paymentStatus === 'failed' && (
              <Alert
                message="支付失败"
                description="支付失败或已超时，请重新生成二维码"
                type="error"
                showIcon
              />
            )}

            {/* 二维码 */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px' }}>正在生成支付二维码...</div>
              </div>
            ) : paymentData && paymentData.qrCode ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  background: '#fff', 
                  padding: '20px', 
                  borderRadius: '12px',
                  display: 'inline-block',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                  <QRCodeCanvas 
                    value={paymentData.qrCode} 
                    size={200}
                    level="M"
                    includeMargin={true}
                  />
                </div>
                <div style={{ marginTop: '16px', color: '#666' }}>
                  支付金额：¥{paymentData.amount}
                </div>
              </div>
            ) : null}

            {/* 操作按钮 */}
            <div style={{ marginTop: '24px' }}>
              <Space size="middle">
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={regenerateQR}
                  disabled={loading}
                >
                  重新生成
                </Button>
                <Button 
                  icon={<ArrowLeftOutlined />}
                  onClick={backToMiniProgram}
                >
                  返回
                </Button>
              </Space>
            </div>

            {/* 支付说明 */}
            <div style={{ 
              background: '#f6f8fa', 
              padding: '16px', 
              borderRadius: '8px',
              textAlign: 'left'
            }}>
              <Text strong>支付说明：</Text>
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                <div>1. 使用微信扫描上方二维码</div>
                <div>2. 在微信中确认支付金额</div>
                <div>3. 完成支付后自动激活会员卡</div>
                <div>4. 支付成功后会自动跳转</div>
              </div>
            </div>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default QRPayment;
